<template>
    <div
        class="product-variation"
        @click.prevent.stop="() => {}"
    >
        <span
            v-if="showTitle"
            class="label"
            >Metal: {{ selected.primaryMetal.translation.name }}</span
        >

        <ul
            class="metal-variations"
            role="listbox"
            aria-label="Metal variations"
        >
            <li
                v-for="(variation, index) in variations"
                :key="index"
                :class="
                    getClassMetalVariation(variation.primaryMetal.systemCode)
                "
                class="variation"
                role="option"
                :aria-label="variation.primaryMetal.translation.name"
                :aria-selected="
                    selected.primaryMetal.systemCode ===
                    variation.primaryMetal.systemCode
                "
            >
                <ThLink
                    v-qa="
                        variation.primaryMetal.translation.name.replace(
                            'ct',
                            'K'
                        )
                    "
                    :to="getLink(variation)"
                    class="link"
                    :class="getClassLink(variation)"
                    @click.stop.prevent="$emit('click', variation)"
                >
                    {{ variation.primaryMetal.translation.name }}
                </ThLink>
            </li>
        </ul>
    </div>
</template>

<script lang="ts" setup generic="T extends MetalVariationFragment">
import { useRoutes } from '@/router/routes'
import type { MetalVariationFragment, ProductLinkFragment } from '@/graphql/api'
import { getClassMetalVariation } from '@/views/product/getClassMetalVariation'

import ThLink from '@/components/ui/buttons/ThLink.vue'

const routes = useRoutes()

const props = withDefaults(
    defineProps<{
        selected: T
        variations: (T & ProductLinkFragment)[]
        link?: boolean
        showTitle?: boolean
    }>(),
    {
        link: true,
        showTitle: true,
    }
)

defineEmits<{
    click: [item: T]
}>()

const getLink = (variation: T & ProductLinkFragment) => {
    if (!props.link) {
        return undefined
    }

    return routes.ring.product(variation)
}

const getClassLink = (variation: T) => ({
    'router-link-active':
        props.selected.primaryMetal.systemCode ===
        variation.primaryMetal.systemCode,
})
</script>

<style lang="scss" scoped>
.product-variation {
    padding: 0 10px 15px 0;
    cursor: default;
}

.metal-variations {
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .variation {
        position: relative;
        width: 28px;
        height: 28px;
        margin: 10px 10px 0 0;

        cursor: pointer;
        font-family: $effra-font-stack;
        text-transform: uppercase;
        font-size: 0.75rem;
        letter-spacing: 0.05em;

        .link {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: #c8c8c8;
            font-size: 0;
            display: block;

            &.router-link-active {
                border: 1px solid $blue;
                box-shadow: inset 0 0 0 3px $white;
            }
        }

        &::before {
            pointer-events: none;
            color: $slate;
            position: absolute;
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        &.eighteen::before {
            content: '18';
        }

        &.fourteen::before {
            content: '14';
        }

        &.nine ::before {
            content: '9';
        }

        &.white-gold .link {
            background: #c8c8c8;
        }

        &.yellow-gold .link {
            background: #e7d698;
        }

        &.rose-gold .link {
            background: #efcaad;
        }

        &.platinum {
            &::before {
                content: 'PT';
            }

            .link {
                background: #efefef;
            }
        }
    }
}
</style>
